/**
 * Filter utility functions for CarwizDataGrid
 * Provides generic multi-filter logic that can be applied to any dataset
 */

/**
 * Apply multiple filters to a dataset
 * @param {Array} rows - Array of data rows to filter
 * @param {Array} filterItems - Array of filter items with { columnField, operatorValue, value }
 * @returns {Array} Filtered rows
 */
export const applyMultiFilters = (rows, filterItems) => {
  // If no filters, return all rows
  if (!filterItems || filterItems.length === 0) {
    return rows;
  }

  // Apply all filters (AND logic - row must match all filters)
  return rows.filter((row) => {
    return filterItems.every((filterItem) => {
      const {
        columnField: field,
        operatorValue: operator,
        value
      } = filterItem;

      // Skip incomplete filters
      if (!field || !operator) {
        return true;
      }

      const cellValue = row[field];

      // Handle empty value checks first
      if (operator === 'isEmpty') {
        return cellValue == null || cellValue === '';
      }
      if (operator === 'isNotEmpty') {
        return cellValue != null && cellValue !== '';
      }

      // For other operators, if cell is empty, it doesn't match (unless explicitly checking for empty)
      if (cellValue == null || cellValue === '') {
        return false;
      }

      const cellValueStr = String(cellValue).toLowerCase();

      switch (operator) {
        case 'contains':
          const containsValue = String(value || '').toLowerCase();
          return cellValueStr.includes(containsValue);
        case 'equals':
          const equalsValue = String(value || '').toLowerCase();
          return cellValueStr === equalsValue;
        case 'startsWith':
          const startsWithValue = String(value || '').toLowerCase();
          return cellValueStr.startsWith(startsWithValue);
        case 'endsWith':
          const endsWithValue = String(value || '').toLowerCase();
          return cellValueStr.endsWith(endsWithValue);
        case 'isAnyOf':
          if (Array.isArray(value) && value.length > 0) {
            return value.some(
              (v) =>
                String(cellValue).toLowerCase() === String(v).toLowerCase()
            );
          }
          return false;
        default:
          return true;
      }
    });
  });
};
